import { Debug, LogCategory } from '@shared/Debug';

export class RoomInfo {
  private container: HTMLDivElement;
  private roomIdElement: HTMLSpanElement;
  private shareButton: HTMLButtonElement;

  private roomId: string;
  
  constructor() {
    // Create container for room ID display
    this.container = document.createElement('div');
    this.container.style.position = 'fixed';
    this.container.style.bottom = '20px';
    this.container.style.left = '140px';
    this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    this.container.style.color = 'white';
    this.container.style.padding = '8px 16px';
    this.container.style.borderRadius = '6px';
    this.container.style.fontFamily = 'Arial, sans-serif';
    this.container.style.fontSize = '14px';
    this.container.style.zIndex = '9999';
    this.container.style.display = 'flex';
    this.container.style.alignItems = 'center';
    this.container.style.gap = '8px';
    this.container.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
    this.container.style.border = '1px solid rgba(255, 255, 255, 0.1)';
    this.container.style.cursor = 'pointer';
    this.container.style.transition = 'background-color 0.2s';
    // Create room ID text
    this.roomIdElement = document.createElement('span');
    this.roomIdElement.style.whiteSpace = 'nowrap';
    this.roomIdElement.textContent = 'Connecting...';
    this.container.appendChild(this.roomIdElement);
    
    // Create share button with icon
    this.shareButton = document.createElement('button');
    this.shareButton.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
      <polyline points="16 6 12 2 8 6"></polyline>
      <line x1="12" y1="2" x2="12" y2="15"></line>
    </svg>`;
    this.shareButton.style.backgroundColor = 'transparent';
    this.shareButton.style.color = 'white';
    this.shareButton.style.border = 'none';
    this.shareButton.style.borderRadius = '4px';
    this.shareButton.style.padding = '4px';   
    this.shareButton.style.display = 'flex';
    this.shareButton.style.alignItems = 'center';
    this.shareButton.style.justifyContent = 'center';
    
    
    this.container.addEventListener('mouseenter', () => {
      this.container.style.backgroundColor = 'rgba(55, 55, 55, 1)';
    });
    
    this.container.addEventListener('mouseleave', () => {
      this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    });
    
    this.container.addEventListener('pointerdown', async () => {
      if (this.roomId) {
        // Check if Web Share API is supported
        if (navigator.share && typeof navigator.share === 'function') {
          try {
            await navigator.share({
              title: 'Play Hoops! with me',
              text: `Join my game using room ID: ${this.roomId}`,
              url: `${window.location.href}?roomId=${this.roomId}`
            });
          } catch (err) {
            console.log('Share cancelled or failed, falling back to clipboard');
            await this.copyToClipboard();
          }
        } else {
          // Fallback for browsers without Web Share API
          await this.copyToClipboard();
        }
      }
    });
    
    this.container.appendChild(this.shareButton);
    
    // Initially hide the share button until we have a room ID
    this.shareButton.style.display = 'none';
    
    // Add to document
    document.body.appendChild(this.container);
  }
  
  /**
   * Update the room ID displayed
   * @param roomId The room ID to display
   */
  public updateRoomId(roomId: string | null): void {
    Debug.info(`Updating room ID display to: ${roomId}`, LogCategory.GENERAL);

    if (!roomId) {
      this.roomIdElement.textContent = 'Unavailable';
      this.shareButton.style.display = 'none';
      return;
    }
    this.roomId = roomId;
    
    this.roomIdElement.textContent = `Invite`;
    this.shareButton.style.display = 'inline-block';
    
    // Make sure the container is visible
    this.container.style.display = 'flex';
  }
  
  /**
   * Dispose of the room info element
   */
  public dispose(): void {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  /**
   * Helper method to copy the room URL to clipboard
   */
  private async copyToClipboard(): Promise<void> {
    const shareUrl = `${window.location.href}?roomId=${this.roomId}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.cssText = 'position:fixed;left:-999px';
        document.body.appendChild(textarea);
        textarea.select();
        // @ts-ignore -- intentionally using deprecated API as fallback
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      
      // Show success feedback
      this.shareButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
      setTimeout(() => {
        this.shareButton.style.backgroundColor = 'transparent';
      }, 200);
    } catch (err) {
      console.log('Error copying to clipboard:', err);
    }
  }
} 